import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Title);

// ===== Styled =====
const Wrapper = styled.div`
  position: relative;
  padding: 1rem;
  background: #f9f9f9;

  /* Fill parent card */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Controls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  padding: 12px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const CanvasWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 14px;
  color: #666;
  cursor: pointer;
  z-index: 5;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

// ===== Colors (purchase palette) =====
const PURPLE = "#9848c7";                       // current bars
const PURPLE_BORDER = "rgba(152,72,199,1)";
const LIGHT_PURPLE = "rgba(152,72,199,0.5)";    // current trend (dashed)
const RED = "rgba(220,53,69,1)";                // previous line
const RED_FILL = "rgba(220,53,69,0.15)";

// ===== Helpers =====
function getWeekNumber(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const firstJan = new Date(d.getFullYear(), 0, 1);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((d - firstJan) / dayMs) + 1;
  return Math.ceil(diffDays / 7); // 1..53
}

function rollingAverage(arr, window = 4) {
  const out = new Array(arr.length).fill(NaN);
  let sum = 0, count = 0;
  for (let i = 0; i < arr.length; i++) {
    const v = Number.isFinite(arr[i]) ? arr[i] : 0;
    sum += v;
    count++;
    if (i >= window) {
      const old = Number.isFinite(arr[i - window]) ? arr[i - window] : 0;
      sum -= old;
      count--;
    }
    if (i >= window - 1) out[i] = sum / window; // fixed window size
  }
  return out;
}

// Replace null/undefined with NaN (Chart.js-safe gap)
const sanitize = (v) => (v === null || typeof v === "undefined" ? NaN : v);

const ZakupyTygodnioweZTrendami = ({ data }) => {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);

  useEffect(() => {
    const ys = Array.from(
      new Set(
        (data || [])
          .map((r) => {
            const d = new Date(r.DataZakupu);
            return isNaN(d) ? null : d.getFullYear();
          })
          .filter(Boolean)
      )
    ).sort((a, b) => a - b);
    setYears(ys);
    if (!year && ys.length) setYear(ys[ys.length - 1]);
  }, [data]);

  const chartData = useMemo(() => {
    if (!year) return null;

    // Weekly sums for current and previous year
    const wkCur = Array(53).fill(0);      // index 0 unused; weeks 1..52
    const wkPrev = Array(53).fill(null);  // keep gaps; we'll sanitize to NaN later

    (data || []).forEach((row) => {
      const d = new Date(row.DataZakupu);
      if (isNaN(d)) return;

      const raw = parseFloat(row.K_42);
      const v = Number.isFinite(raw) ? raw : 0;

      // Skip NEGATIVE day values completely (keep zeroes)
      if (v < 0) return;

      const w = getWeekNumber(row.DataZakupu);
      if (!w || w > 52) return;

      const y = d.getFullYear();
      if (y === year) {
        wkCur[w] = (wkCur[w] || 0) + v;
      } else if (y === year - 1) {
        wkPrev[w] = (wkPrev[w] || 0) + v;
      }
    });

    const labels = Array.from({ length: 52 }, (_, i) => `Tydz. ${i + 1}`);
    const cur52 = wkCur.slice(1, 53).map((v) => v || 0);
    const prev52 = wkPrev.slice(1, 53).map(sanitize);

    // Trend over current year (4-week window)
    const trendAll = rollingAverage(cur52, 4);

    // Find last week with current-year data > 0
    const lastWeekCurIdx = (() => {
      for (let i = cur52.length - 1; i >= 0; i--) if (cur52[i] > 0) return i;
      return -1;
    })();

    // Hide current bars & trend after the last positive week → use NaN (not null)
    const curDisplay = cur52.map((v, i) => (i <= lastWeekCurIdx ? v : NaN));
    const trendCur = trendAll.map((v, i) => (i <= lastWeekCurIdx ? v : NaN));

    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: `Zakupy (${year})`,
          data: curDisplay,
          backgroundColor: "rgba(152,72,199,0.85)",
          borderColor: PURPLE_BORDER,
          borderWidth: 1,
          order: 2
        },
        {
          type: "line",
          label: `Zakupy (${year - 1})`,
          data: prev52, // NaNs mark gaps
          borderColor: RED,
          backgroundColor: RED_FILL,
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          tension: 0.25,
          order: 1
        },
        {
          type: "line",
          label: `Trend (${year}) – 4 tyg.`,
          data: trendCur,
          borderColor: LIGHT_PURPLE,
          borderWidth: 2,
          borderDash: [8, 4],
          pointRadius: 0,
          tension: 0.25,
          order: 3
        }
      ]
    };
  }, [data, year]);

  if (!year || !chartData) return null;

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="weekly-purchases-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="weekly-purchases-tooltip"
        place="left"
        content={
          <>
            <strong>Zakupy tygodniowe z trendami</strong><br />
            Fioletowe słupki: {year}. Czerwona linia: {year - 1}.<br />
            Jasny fiolet (kreskowana): 4-tyg. trend {year}.<br />
            Wartości &lt; 0 są pomijane; oś Y od 0.
          </>
        }
      />

      <Controls>
        <label>
          Rok:&nbsp;
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </Controls>

      <Card>
        <CanvasWrap>
          <Bar
            key={`${year}-${chartData.labels.length}`}  // avoid "canvas already in use" on remount
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "top" },
                title: {
                  display: true,
                  text: `Zakupy tygodniowe z trendami – ${year} vs ${year - 1}`
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const v = ctx.parsed?.y ?? ctx.raw ?? 0;
                      if (!Number.isFinite(v)) return null; // don't show tooltips for NaN gaps
                      return `${ctx.dataset.label}: ${Number(v).toLocaleString("pl-PL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}`;
                    }
                  }
                }
              },
              scales: {
                x: { grid: { display: false } },
                y: {
                  beginAtZero: true,
                  min: 0, // never show negative axis
                  title: { display: true, text: "Zakupy (PLN)" },
                  ticks: {
                    callback: (val) =>
                      Number(val).toLocaleString("pl-PL", { minimumFractionDigits: 0 })
                  }
                }
              }
            }}
          />
        </CanvasWrap>
      </Card>
    </Wrapper>
  );
};

export default ZakupyTygodnioweZTrendami;
